import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMarketplaceVmLogs, getMarketplaceVmStatus } from '../services/api';
import './VmMonitoring.css';

const PROBLEM_STATES = new Set(['blocked', 'error', 'stale']);

function relativeTime(iso) {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

function Led({ kind, state }) {
  const cls = kind === 'power'
    ? (state === 'running' ? 'ok' : state === 'stopped' ? 'off' : 'unknown')
    : (state === 'healthy' ? 'ok' : state === 'off_hours' || state === 'powered_off' || state === 'unknown' ? 'off' : 'bad');
  return (
    <span className={`vm-led vm-led--${cls}`} title={`${kind}: ${state || 'unknown'}`} />
  );
}

function emptyLogMessage(vm) {
  if (!vm.last_seen) {
    return 'No heartbeat on this backend. Bots post to production Capture; this dashboard is capture-dev.';
  }
  if (vm.display_state === 'stale') {
    return `Last heartbeat was ${relativeTime(vm.last_seen)} (stale). No log lines were stored with it. New logs only appear after a fresh POST — run the bot without --test, and confirm it posts to this dashboard's API.`;
  }
  return 'Heartbeat is live, but log_tail is empty.';
}

function matchesQuery(vm, q) {
  if (!q) return true;
  const hay = [vm.name, vm.assistant_id, vm.hostname, vm.gcp_instance]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

const VmMonitoring = () => {
  const [vms, setVms] = useState([]);
  const [counts, setCounts] = useState({ healthy: 0, problem: 0, off_hours: 0, total: 0 });
  const [gcpError, setGcpError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [logs, setLogs] = useState([]);
  const logPaneRef = useRef(null);
  const stickBottom = useRef(true);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await getMarketplaceVmStatus();
      const data = response.data || {};
      setVms(data.vms || []);
      setCounts(data.counts || { healthy: 0, problem: 0, off_hours: 0, total: 0 });
      setGcpError(data.gcp_error || null);
      setError('');
    } catch (err) {
      setError('Failed to load VM status.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async (assistantId) => {
    if (!assistantId) return;
    try {
      const response = await getMarketplaceVmLogs(assistantId);
      setLogs(response.data?.log_tail || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setLogs(['No heartbeat stored on this backend for this VM.']);
      } else {
        setLogs([`Failed to load logs (${status || 'network error'}).`]);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const timer = setInterval(() => fetchStatus(true), 10000);
    return () => clearInterval(timer);
  }, [fetchStatus]);

  useEffect(() => {
    if (!expandedId) {
      setLogs([]);
      return undefined;
    }
    fetchLogs(expandedId);
    const timer = setInterval(() => fetchLogs(expandedId), 3000);
    return () => clearInterval(timer);
  }, [expandedId, fetchLogs]);

  useEffect(() => {
    const pane = logPaneRef.current;
    if (pane && stickBottom.current) {
      pane.scrollTop = pane.scrollHeight;
    }
  }, [logs]);

  const onLogScroll = () => {
    const pane = logPaneRef.current;
    if (!pane) return;
    const fromBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
    stickBottom.current = fromBottom < 40;
  };

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      // ignore
    }
  };

  const query = search.trim().toLowerCase();
  const visible = vms.filter((vm) => {
    if (problemsOnly && !PROBLEM_STATES.has(vm.display_state)) return false;
    return matchesQuery(vm, query);
  });

  return (
    <div className="vm-monitoring">
      <h2>Marketplace VMs</h2>
      <p className="vm-monitoring__sub">
        Bot health from heartbeats, power from GCP. Overnight shutdown is expected (gray).
      </p>

      <div className="vm-summary">
        <span><strong>{counts.healthy}</strong> healthy</span>
        <span className={counts.problem ? 'vm-summary--bad' : ''}><strong>{counts.problem}</strong> problem</span>
        <span><strong>{counts.off_hours}</strong> off</span>
        <span>{counts.total} total</span>
      </div>

      <div className="vm-controls">
        <input
          className="vm-search"
          type="text"
          placeholder="Search name, assistant_id, hostname…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="vm-filter">
          <input
            type="checkbox"
            checked={problemsOnly}
            onChange={(e) => setProblemsOnly(e.target.checked)}
          />
          Problems only
        </label>
        <button type="button" className="vm-refresh" onClick={() => fetchStatus()}>Refresh</button>
      </div>

      {gcpError && <p className="vm-banner">GCP power lookup failed: {gcpError}. Bot LEDs still work.</p>}
      {error && <p className="vm-banner vm-banner--error">{error}</p>}
      {loading && <p>Loading…</p>}

      <div className="vm-list">
        {visible.map((vm) => {
          const open = expandedId === vm.assistant_id;
          const schedule = (vm.start_hour != null && vm.end_hour != null)
            ? `${String(vm.start_hour).padStart(2, '0')}:00–${String(vm.end_hour).padStart(2, '0')}:59`
            : 'no schedule';
          return (
            <div key={vm.assistant_id} className={`vm-row ${PROBLEM_STATES.has(vm.display_state) ? 'vm-row--problem' : ''}`}>
              <button
                type="button"
                className="vm-row__header"
                onClick={() => {
                  stickBottom.current = true;
                  setExpandedId(open ? null : vm.assistant_id);
                }}
              >
                <span className="vm-row__chevron">{open ? '▼' : '▶'}</span>
                <Led kind="power" state={vm.power} />
                <Led kind="bot" state={vm.bot_state || vm.display_state} />
                <span className="vm-row__name">{vm.name}</span>
                <span className={`vm-badge vm-badge--${vm.display_state}`}>{vm.display_state.replace(/_/g, ' ')}</span>
                <span className="vm-row__meta">{vm.hostname || '—'}</span>
                <span className="vm-row__meta">{relativeTime(vm.last_seen)}</span>
                <span className="vm-row__meta">{schedule}</span>
                <span className="vm-row__meta">iter {vm.iteration ?? '—'}</span>
                {vm.last_error && <span className="vm-row__error">{vm.last_error}</span>}
              </button>
              {open && (
                <div className="vm-row__body">
                  <div className="vm-details">
                    <div>
                      <code>{vm.assistant_id}</code>
                      <button type="button" className="vm-linkish" onClick={() => copyId(vm.assistant_id)}>Copy ID</button>
                      <Link className="vm-linkish" to={`/?assistant_id=${encodeURIComponent(vm.assistant_id)}`}>Open agent</Link>
                    </div>
                    <div>URL: {vm.url_kind || '—'} · TZ: {vm.timezone || '—'} · GCP: {vm.gcp_instance || 'unknown'}{vm.gcp_zone ? ` (${vm.gcp_zone})` : ''}</div>
                    {vm.pipeline_category && <div>Last pipeline: {vm.pipeline_category}</div>}
                    {vm.details && <div>{vm.details}</div>}
                  </div>
                  <pre
                    ref={logPaneRef}
                    className="vm-logs"
                    onScroll={onLogScroll}
                  >{(logs.length ? logs : [emptyLogMessage(vm)]).join('\n')}</pre>
                </div>
              )}
            </div>
          );
        })}
        {!loading && visible.length === 0 && <p className="vm-empty">No Marketplace VMs match.</p>}
      </div>
    </div>
  );
};

export default VmMonitoring;
